import { createContext, useContext, useId, forwardRef, type ReactNode, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

/**
 * Form controls: rounded boxes that turn blue on focus (27 Oct 2026).
 *
 * `Field` supplies the id, the describedby wiring and the error styling to
 * whatever control sits inside it, so no screen has to remember the ARIA.
 */

interface FieldContextValue {
  id: string
  errorId: string | undefined
  hintId: string | undefined
  invalid: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

function useField(): FieldContextValue | null {
  return useContext(FieldContext)
}

export interface FieldProps {
  label: string
  /** Sits under the control. Say what a good value looks like, not what the field is. */
  hint?: string
  error?: string | undefined
  required?: boolean
  /** Right-aligned note in the label row — a unit, a count, a "leave blank to skip". */
  aside?: ReactNode
  className?: string
  children: ReactNode
}

export function Field({ label, hint, error, required, aside, className, children }: FieldProps) {
  const id = useId()
  const errorId = error ? `${id}-error` : undefined
  const hintId = hint ? `${id}-hint` : undefined

  return (
    <FieldContext.Provider value={{ id, errorId, hintId, invalid: Boolean(error) }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor={id} className="label">
            {label}
            {required ? <span className="text-clay not-italic"> *</span> : null}
          </label>
          {aside ? <span className="text-[length:var(--text-2xs)] text-taupe">{aside}</span> : null}
        </div>

        {children}

        {error ? (
          <p id={errorId} role="alert" className="text-[length:var(--text-2xs)] text-clay">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-[length:var(--text-2xs)] text-taupe">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

const CONTROL =
  'w-full rounded-md bg-[var(--surface-card)] px-2.5 py-1.5 text-[length:var(--text-base)] ' +
  'border border-[var(--color-rule-strong)] ' +
  'transition-colors duration-[var(--dur-fast)] ' +
  'hover:border-taupe focus:border-accent focus:outline-none focus-visible:outline-none ' +
  'disabled:opacity-50 disabled:hover:border-[var(--color-rule-strong)]'

const INVALID = 'border-clay hover:border-clay focus:border-clay'

/** Focus: a blue border, doubled with an inset ring so it reads clearly. */
const FOCUS_RING = 'focus:shadow-[inset_0_0_0_1px_var(--color-accent)]'

function controlProps(field: FieldContextValue | null, invalidProp?: boolean) {
  const invalid = field?.invalid ?? invalidProp ?? false
  return {
    id: field?.id,
    'aria-invalid': invalid || undefined,
    'aria-describedby': [field?.errorId, field?.hintId].filter(Boolean).join(' ') || undefined,
    invalid,
  }
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, ...rest }, ref) {
  const field = useField()
  const { invalid, ...aria } = controlProps(field)

  return <input ref={ref} {...aria} className={cn(CONTROL, FOCUS_RING, invalid && INVALID, className)} {...rest} />
})

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, ...rest },
  ref,
) {
  const field = useField()
  const { invalid, ...aria } = controlProps(field)

  return (
    <textarea
      ref={ref}
      rows={rows}
      {...aria}
      className={cn(
        CONTROL,
        'resize-y leading-relaxed py-2',
        FOCUS_RING,
        invalid && 'border-clay hover:border-clay focus:border-clay',
        className,
      )}
      {...rest}
    />
  )
})

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, children, ...rest }, ref) {
  const field = useField()
  const { invalid, ...aria } = controlProps(field)

  return (
    <select
      ref={ref}
      {...aria}
      className={cn(
        CONTROL,
        // The native arrow is replaced with a hairline chevron so the control
        // matches everything else rather than inheriting the OS look.
        "appearance-none bg-no-repeat bg-[right_0.6rem_center] pr-7 " +
          "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='6' viewBox='0 0 9 6'%3E%3Cpath d='M1 1l3.5 3.5L8 1' stroke='%236b6862' fill='none' stroke-width='1.4'/%3E%3C/svg%3E\")]",
        invalid && INVALID,
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  )
})

/**
 * A tick box: a rounded square that turns blue (the `check` class, index.css). The native control
 * is kept and restyled rather than replaced, so keyboard and screen-reader
 * behaviour is the browser's, not ours.
 */
export function Checkbox({
  label,
  description,
  className,
  ...rest
}: { label: string; description?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const id = useId()

  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        id={id}
        type="checkbox"
        className={
          'check mt-0.5 size-[16px] shrink-0 transition-colors duration-[var(--dur-fast)] disabled:opacity-40'
        }
        {...rest}
      />
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className="cursor-pointer text-[length:var(--text-sm)] leading-tight">
          {label}
        </label>
        {description ? <p className="text-[length:var(--text-2xs)] text-taupe">{description}</p> : null}
      </div>
    </div>
  )
}

/**
 * A prefix that sits inside the box — 'R' on a money field, 'km' after a
 * distance. Keeps the unit out of the value so parsing stays trivial.
 */
export function Affixed({
  prefix,
  suffix,
  children,
  className,
}: {
  prefix?: string
  suffix?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-md border border-[var(--color-rule-strong)] bg-[var(--surface-card)] px-2.5 focus-within:border-accent',
        className,
      )}
    >
      {prefix ? <span className="text-[length:var(--text-base)] text-taupe">{prefix}</span> : null}
      <div className="flex-1 [&>input]:border-0 [&>input]:px-0 [&>input]:shadow-none [&>input]:focus:shadow-none">{children}</div>
      {suffix ? <span className="text-[length:var(--text-2xs)] text-taupe">{suffix}</span> : null}
    </div>
  )
}
